import { AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ResetPasswordFormInterface } from './reset-password-form.interface';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { getSubscription, Match } from '@platri/elab-angular-core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { dfAppRoute, dfLoginRoute, dfResetPasswordTokenRouterParam, noWhitespaceValidator, UserHttpService } from '@platri/dfx-angular-core';
import { emailRegex } from '@platri/df-common-core';

@Component({
  selector: 'elab-reset-password-form',
  templateUrl: './reset-password-form.component.html',
  styleUrls: ['./reset-password-form.component.scss']
})
export class ResetPasswordFormComponent implements OnDestroy, OnInit, AfterContentInit {
  @Output() successfulResetPasswordEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  resetPasswordFormGroup!: FormGroup<ResetPasswordFormInterface>;

  subscriptions: Subscription = new Subscription();

  isInitialized = false;
  isWaitingForResetPasswordResponse = false;
  hasFatalErrorOnInitialize = false;
  hasNonFatalErrorOnInitialize = false;

  resetPasswordFailedWrongData = false;
  resetPasswordFailedNoConnection = false;
  resetPasswordFailedInternalServer = false;

  hidePassword = true;
  
  hasSuccessfullyResetPassword = false;

  routerParams!: Params;

  constructor(
    private fb: FormBuilder,
    private userHttpService: UserHttpService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private matSnackbar: MatSnackBar
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.initializeFormGroup();
  }

  ngAfterContentInit(): void {
    this.initializeFormSubscriptions();
    this.initializeSubscriptions();
    this.isInitialized = true;
  }

  initializeFormGroup(): void {
    this.resetPasswordFormGroup = this.createResetPasswordFormGroup();
  }

  createResetPasswordFormGroup(): FormGroup<ResetPasswordFormInterface> {
    return this.fb.group<ResetPasswordFormInterface>({
        email: this.fb.control('', [Validators.required, Validators.email, noWhitespaceValidator(), Validators.pattern(emailRegex)]),
        password: this.fb.control('', [Validators.required]),
        confirmPassword: this.fb.control('', [Validators.required])
      },
      {
        validators: [Match('password', 'confirmPassword')]
      }
    );
  }

  initializeFormSubscriptions(): void {
    this.initializeOnValueChangesSubscription();
  }

  initializeOnValueChangesSubscription(): void {
    this.subscriptions.add(this.resetPasswordFormGroup.valueChanges.subscribe(res => {
      this.resetPasswordFailedWrongData = false;
      this.resetPasswordFailedNoConnection = false;
      this.resetPasswordFailedInternalServer = false;
    }));
  }

  initializeSubscriptions(): void {
    this.subscriptions.add(getSubscription(this.activatedRoute.params, this.onActivatedRouteChanges.bind(this)));
  }

  onActivatedRouteChanges(params: Params): void {
    this.routerParams = params;
    if (!this.routerParams[dfResetPasswordTokenRouterParam]) {
      this.router.navigate([dfAppRoute, dfLoginRoute]);
    }
  }

  onSubmit(): void {
    if (this.resetPasswordFormGroup.valid) {
      this.isWaitingForResetPasswordResponse = true;
      this.resetPasswordFailedWrongData = false;
      this.resetPasswordFailedNoConnection = false;
      this.resetPasswordFailedInternalServer = false;
      const {email, password} = this.resetPasswordFormGroup.getRawValue();
      // const resetPasswordRequestDto: ResetPasswordRequestDto = {email: email!, password: password!, token: this.routerParams[kwResetPasswordTokenRouterParam]};
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.subscriptions.add(this.userHttpService.resetPassword(email!, this.routerParams[dfResetPasswordTokenRouterParam]!, password!).subscribe(res => {
        this.hasSuccessfullyResetPassword = true;
        this.isWaitingForResetPasswordResponse = false;
        this.matSnackbar.open("Successfully password reset!");
      }, error => {
        console.log(error);
        if (error.status === 0) {
          this.onConnectionLost();
        }
        if (error.status === 500) {
          this.onInternalServerError();
        }
        this.isWaitingForResetPasswordResponse = false;
      }));
    }
  }

  onConnectionLost(): void {
    (this.resetPasswordFailedNoConnection) = true;
  }
  
  onInternalServerError(): void {
    this.resetPasswordFailedInternalServer = true;
  }
  
  routeToLogin(): void {
    this.router.navigate(['/' + dfAppRoute, dfLoginRoute]);
    this.successfulResetPasswordEmitter.next(true);
  }
  
}
